import { useMemo } from 'react';
import { Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Chart } from '@vnetwork/ui/charts';
import { useDashboardStats } from '@/api/capacities';
import { CapacitySectionCard } from './capacity-section-card';
import { PhysicalStatCards } from './physical-stat-cards';
import { toStatusBars, toLocationBars } from './physical-charts';
import { PhysicalExtraSections } from './physical-extra-sections';

const GRID_2: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--vn-space-4)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
};

/**
 * Physical Inventory tab — warehouse stat cards plus status/location charts,
 * with the remaining category/value/warranty/supplier/age/trend/activity
 * sections delegated to {@link PhysicalExtraSections}. Mirrors the legacy
 * Warehouse dashboard. Read-only.
 */
export function PhysicalInventoryTab() {
  const { data, isLoading, isError } = useDashboardStats();

  const equipmentByStatus = useMemo(() => toStatusBars(data?.equipment_by_status), [data]);
  const componentsByStatus = useMemo(() => toStatusBars(data?.components_by_status), [data]);
  const equipmentByLocation = useMemo(() => toLocationBars(data?.equipment_by_location), [data]);

  if (isError) {
    return (
      <EmptyState
        title="Failed to load inventory"
        description="Warehouse statistics are unavailable right now."
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Skeleton style={{ height: 110 }} />
        <Skeleton style={{ height: 280 }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <PhysicalStatCards data={data} />

      <div style={GRID_2}>
        <CapacitySectionCard title="Equipment by Status" description="Current equipment lifecycle state">
          {equipmentByStatus.rows.length > 0 ? (
            <Chart
              type="bar"
              stacked
              height={260}
              data={equipmentByStatus.rows}
              xKey="label"
              series={equipmentByStatus.series}
              valueFormatter={(v) => `${v ?? 0}`}
            />
          ) : (
            <EmptyState size="sm" title="No equipment data" />
          )}
        </CapacitySectionCard>

        <CapacitySectionCard title="Components by Status" description="Current component lifecycle state">
          {componentsByStatus.rows.length > 0 ? (
            <Chart
              type="bar"
              stacked
              height={260}
              data={componentsByStatus.rows}
              xKey="label"
              series={componentsByStatus.series}
              valueFormatter={(v) => `${v ?? 0}`}
            />
          ) : (
            <EmptyState size="sm" title="No component data" />
          )}
        </CapacitySectionCard>
      </div>

      <CapacitySectionCard
        title="Equipment Distribution by Location"
        description="Equipment count per warehouse / datacenter"
      >
        {equipmentByLocation.rows.length > 0 ? (
          <Chart
            type="bar"
            stacked
            height={300}
            data={equipmentByLocation.rows}
            xKey="label"
            series={equipmentByLocation.series}
            valueFormatter={(v) => `${v ?? 0}`}
          />
        ) : (
          <EmptyState size="sm" title="No location data" />
        )}
      </CapacitySectionCard>

      <PhysicalExtraSections data={data} />
    </div>
  );
}
